import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Fairfield", cities: "Centro de Fairfield, Cordelia, Green Valley, Rockville" },
    { zone: "Suisun City", cities: "Suisun City, Suisun Marsh, Tolenas" },
    { zone: "Vacaville", cities: "Vacaville, Dixon, Elmira, Browns Valley" },
    { zone: "Vallejo", cities: "Vallejo, Benicia, American Canyon" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Vecindarios que Servimos en Fairfield</h2>
          <p className="text-muted-foreground">Limpieza profesional en Fairfield, Suisun City, Vacaville y todo el Condado de Solano.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div key={i} className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")} style={{ transitionDelay: `${i * 100}ms` }}>
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Fairfield</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const FairfieldSpanishPage = () => (
  <LocationContext.Provider value={{ slug: "fairfield", name: "Fairfield", type: "city", regionSlug: "solano" }}>
    <SEO
      title="Limpieza del Hogar en Fairfield, CA — Uplevel Cleaning"
      description="Servicio profesional de limpieza del hogar en Fairfield, CA. Precios planos, reserva en 60 segundos, garantía del 200%. Limpieza estándar, profunda y de mudanza."
      keywords="limpieza fairfield ca, maid service fairfield español, servicio de limpieza fairfield ca, empleada doméstica fairfield, limpieza suisun city"
      url="https://uplevelcleaning.com/es/fairfield"
      locationName="Fairfield"
      regionSlug="solano"
      lang="es"
      includeLocalBusiness={true}
      includeFAQ={true}
      breadcrumbs={[{ name: "Inicio", url: "/" }, { name: "Condado de Solano", url: "/es/solano" }, { name: "Fairfield", url: "/es/fairfield" }]}
      reviews={[
        { author: "Norma C.", rating: 5, reviewBody: "Vivo en Fairfield y el servicio de Uplevel es excelente. Llegaron puntualmente y dejaron mi hogar impecable. ¡Lo recomiendo a todos mis vecinos!", datePublished: "2026-03-05" },
        { author: "Arturo D.", rating: 5, reviewBody: "Contraté una limpieza profunda para nuestra casa en Fairfield antes de una reunión familiar. El resultado fue increíble. Precio justo y trabajo excepcional.", datePublished: "2026-01-28" },
      ]}
    />
    <div className="flex min-h-screen flex-col" lang="es">
      <StickyBanner />
      <Navbar />
      <main className="flex-1">
        <SpanishHero cityName="Fairfield" regionSlug="solano" />
        <div className="bg-primary/5 border-b border-primary/10 py-4">
          <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
            {["Servicio Local en Fairfield", "Condado de Solano", "Garantía del 200%", "Reserva en 60 Segundos"].map((item, i) => (
              <span key={i} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> {item}</span>
            ))}
          </div>
        </div>
        <SpanishWhyUs cityName="Fairfield" />
        <NearbyAreas />
        <SpanishServices />

        <FrequencyDiscounts />
        <PricingCalculator />
        <SpanishTestimonials region="solano" />
        <Guarantee />
        <SpanishFAQ cityName="Fairfield" />
        <InternalLinks />
        <SpanishFinalCTA cityName="Fairfield" />
      </main>
      <Footer />
    </div>
  </LocationContext.Provider>
);

export default FairfieldSpanishPage;
